import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useUserPersistentState } from '../../hooks/useUserPersistentState';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Stack,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Close,
  QrCodeScanner,
  Delete,
  CallMade,
  Storage,
  Business,
  Person,
  Inventory,
  Upload,
  Print,
  ArrowBack,
  Send,
  LocalShipping,
  PlayArrow
} from '@mui/icons-material';
import { DataService } from '../../services/data-service';
import { LovService } from '../../services/lov-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import ExcelImportDialog, { ImportRow } from '../../components/ExcelImportDialog';
import VirtualizedMovementTable from '../../components/VirtualizedMovementTable';
import { BarcodeSearchResult, bulkBarcodeService } from '../../services/bulk-barcode-service';
import { AllPropertiesCell } from '../../components/MovementDialogs/SharedComponents';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTab } from '../../contexts/TabContext';

// Reuse interfaces from the dialog
interface StorageUnit {
  id: string;
  name: string;
  code: string;
}

interface Box {
  boxBarcode: string;
  quantity: number;
}

interface InventoryLocation {
  storageUnitId: string;
  storageUnitName: string;
  storageUnitCode: string;
  boxes: Box[];
  independentQuantity?: number;
  totalQuantity: number;
  availableQuantity: number;
  itemName?: string;
  unitId?: string;
}


interface TransferRow {
  id: string;
  barcode: string;
  searchResult: BarcodeSearchResult | null;
  selectedSource?: {
    storageUnitId: string;
    storageUnitName: string;
    boxBarcode?: string; // '' means independent
    availableQuantity: number;
  };
  quantity: number;
  loading: boolean;
  error?: string;
  unitData?: any;
  smartRecommended?: boolean;
  smartSourceSelected?: boolean;
  smartRecommendations?: any[]; // Store recommendations for scoring display
}

interface ClientSupplier {
  id: string;
  name: string;
  type: 'client' | 'supplier';
  code?: string;
  contactInfo?: string;
}

interface MoveOutData {
  name: string;
  refNumber: string;
  notes: string;
  referenceId: string;
  date: string; // Date-only format (YYYY-MM-DD)
}

interface PersistentMoveOutState {
  moveOutData: MoveOutData;
  transferRows: TransferRow[];
  selectedDestination: ClientSupplier | null;
  lastUpdated: number;
}

const normalizeBox = (b?: string | null) => (b === 'independent' ? '' : (b ?? ''));
const SOURCE_KEY_SEP = '||';

const makeSourceKey = (storageUnitId: string, boxBarcode?: string) =>
  `${storageUnitId}${SOURCE_KEY_SEP}${boxBarcode ?? ''}`;

const parseSourceKey = (key: string) => {
  const idx = key.lastIndexOf(SOURCE_KEY_SEP);
  if (idx === -1) return { storageUnitId: '', boxBarcode: '' };
  return {
    storageUnitId: key.slice(0, idx),
    boxBarcode: key.slice(idx + SOURCE_KEY_SEP.length)
  };
};
const NewMoveOutPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { registerTabCloseCallback } = useTab();
  const [searchParams] = useSearchParams();

  // Default persistent state
  const defaultPersistentState: PersistentMoveOutState = {
    moveOutData: {
      name: '',
      refNumber: '',
      notes: '',
      referenceId: '',
      date: new Date().toISOString().split('T')[0] // Default to today's date
    },
    transferRows: [
      { id: '1', barcode: '', searchResult: null, quantity: 1, loading: false }
    ],
    selectedDestination: null,
    lastUpdated: Date.now()
  };

  // Persistent state for user-specific data
  const [persistentState, setPersistentState, clearPersistentState] = useUserPersistentState(
    'move-out-form',
    defaultPersistentState,
    { debounceMs: 1000 } // Save after 1 second of inactivity
  );

  // Extract state from persistent state
  const moveOutData = persistentState.moveOutData;
  const transferRows = persistentState.transferRows;
  const selectedDestination = persistentState.selectedDestination;

  // Helper to update move out data
  const setMoveOutData = (updater: (prev: MoveOutData) => MoveOutData | MoveOutData) => {
    setPersistentState(prev => ({
      ...prev,
      moveOutData: typeof updater === 'function' ? updater(prev.moveOutData) : updater,
      lastUpdated: Date.now()
    }));
  };

  // Helper to update transfer rows
  const setTransferRows = (updater: (prev: TransferRow[]) => TransferRow[] | TransferRow[]) => {
    setPersistentState(prev => ({
      ...prev,
      transferRows: typeof updater === 'function' ? updater(prev.transferRows) : updater,
      lastUpdated: Date.now()
    }));
  };

  // Helper to update selected destination
  const setSelectedDestination = (destination: ClientSupplier | null) => {
    setPersistentState(prev => ({
      ...prev,
      selectedDestination: destination,
      lastUpdated: Date.now()
    }));
  };

  const [clientsSuppliers, setClientsSuppliers] = useState<ClientSupplier[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [destinationsLoaded, setDestinationsLoaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [smartAllocating, setSmartAllocating] = useState(false);
  const [loadingTransferOrder, setLoadingTransferOrder] = useState(false);
  const [transferOrderDialogOpen, setTransferOrderDialogOpen] = useState(false);
  const [transferOrders, setTransferOrders] = useState<any[]>([]);
  const [loadingTransferOrders, setLoadingTransferOrders] = useState(false);
  const [selectedTransferOrder, setSelectedTransferOrder] = useState<any>(null);
  const [transferOrderSearchText, setTransferOrderSearchText] = useState('');

  useEffect(() => {
    if (!destinationsLoaded) {
      loadDestinations();
    }
  }, []);

  // Load transfer order if prepareOrderId is provided
  useEffect(() => {
    const prepareOrderId = searchParams.get('prepareOrderId');
    if (prepareOrderId && destinationsLoaded) {
      loadTransferOrder(prepareOrderId);
    }
  }, [searchParams, destinationsLoaded]);

  // Show notification when user has saved data from previous session
  useEffect(() => {
    const hasExistingData = persistentState.lastUpdated > 0 &&
      (persistentState.moveOutData.name ||
       persistentState.selectedDestination ||
       persistentState.transferRows.some(row => row.barcode.trim()));

    if (hasExistingData) {
      const timeAgo = Math.round((Date.now() - persistentState.lastUpdated) / (1000 * 60)); // minutes
      const timeDisplay = timeAgo < 60
        ? `${timeAgo} minutes ago`
        : timeAgo < 1440
          ? `${Math.round(timeAgo / 60)} hours ago`
          : `${Math.round(timeAgo / 1440)} days ago`;

      dispatch(showAlert({
        message: `Restored your previous move-out progress (saved ${timeDisplay}). You can continue where you left off.`,
        type: 'info'
      }));
    }
  }, []); // Only run once on mount

  // Register tab close cleanup callback
  useEffect(() => {
    registerTabCloseCallback('/movements/new-move-out', () => {
      clearPersistentState();
    });
    // Note: We don't unregister on unmount - we want the callback to persist
    // so it runs when the tab is closed from the tab system
  }, [registerTabCloseCallback, clearPersistentState]);

  const loadDestinations = async () => {
    if (loadingDestinations || destinationsLoaded) return;

    setLoadingDestinations(true);
    try {
      const [clients, suppliers] = await Promise.all([
        LovService.getLovDictionary('all-clients'),
        LovService.getLovDictionary('all-suppliers')
      ]);

      const combinedList: ClientSupplier[] = [
        ...Object.entries(clients || {}).map(([id, name]) => ({
          id,
          name: String(name),
          type: 'client' as const,
          code: id,
          contactInfo: 'Client'
        })),
        ...Object.entries(suppliers || {}).map(([id, name]) => ({
          id,
          name: String(name),
          type: 'supplier' as const,
          code: id,
          contactInfo: 'Supplier'
        }))
      ];

      setClientsSuppliers(combinedList);
      setDestinationsLoaded(true);
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to load clients and suppliers', type: 'warning' }));
      setClientsSuppliers([]);
    } finally {
      setLoadingDestinations(false);
    }
  };

  const loadAllTransferOrders = async () => {
    if (loadingTransferOrders) return;

    setLoadingTransferOrders(true);
    try {
      const response = await DataService.get('api/TransferOrder');
      const result = await response.json();

      if (result?.success && result?.data) {
        // Filter for "New" status orders only (status = 0)
        const newOrders = result.data.filter((order: any) => order.status === 0);
        setTransferOrders(newOrders);
      } else {
        setTransferOrders([]);
      }
    } catch (error: any) {
      console.error('Failed to load transfer orders:', error);
      dispatch(showAlert({
        message: error.message || 'Failed to load transfer orders',
        type: 'error'
      }));
      setTransferOrders([]);
    } finally {
      setLoadingTransferOrders(false);
    }
  };

  const handleLoadTransferOrder = async (transferOrder: any) => {
    if (!transferOrder) return;

    setTransferOrderDialogOpen(false);
    await loadTransferOrder(transferOrder.id);

    // Clear dialog state
    setSelectedTransferOrder(null);
    setTransferOrderSearchText('');
  };

  const loadTransferOrder = async (orderId: string) => {
    if (loadingTransferOrder) return;

    setLoadingTransferOrder(true);
    try {
      const response = await DataService.get(`api/TransferOrder/${orderId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch transfer order');
      }

      const result = await response.json();

      if (!result?.success || !result?.data) {
        throw new Error(result?.message || 'Transfer order not found');
      }

      const orderData = result.data;

      // Find the client in our loaded destinations
      const client = clientsSuppliers.find(cs => cs.id === orderData.clientId && cs.type === 'client');

      if (!client) {
        throw new Error('Client not found for this transfer order');
      }

      // Update move out data
      setMoveOutData((prev) => ({
        ...prev,
        name: orderData.name || `Transfer Order - ${orderData.clientName}`,
        refNumber: orderId,
        notes: orderData.notes || `Preparing transfer order for ${orderData.clientName}`,
        referenceId: orderId,
        date: new Date().toISOString().split('T')[0]
      }));

      // Set destination
      setSelectedDestination(client);

      // Prepare barcodes for bulk search
      const barcodes = orderData.items.map((item: any) => item.barcode);

      if (barcodes.length === 0) {
        dispatch(showAlert({
          message: 'Transfer order has no items',
          type: 'warning'
        }));
        return;
      }

      // Use bulk barcode service to search all items
      const searchResults = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: true,
        includeBoxes: true,
      });

      // Create rows with quantities from transfer order
      const newRows: TransferRow[] = [];
      let currentId = Date.now();

      for (const item of orderData.items) {
        const searchResult = searchResults.find(r => r.barcode === item.barcode);

        if (!searchResult || !searchResult.found || !searchResult.locations || searchResult.locations.length === 0) {
          // Item not found or not in inventory
          dispatch(showAlert({
            message: `${item.barcode} not found in inventory`,
            type: 'warning'
          }));
          continue;
        }

        const normalized: BarcodeSearchResult = {
          type: searchResult.type === 'box' ? 'box' : 'unit',
          itemName: searchResult.itemName || '',
          unitId: searchResult.unitId,
          locations: searchResult.locations,
          unit: searchResult.unit
        };

        newRows.push({
          id: (currentId++).toString(),
          barcode: item.barcode,
          searchResult: normalized,
          quantity: item.expectedQuantity,
          loading: false
        });
      }

      if (newRows.length === 0) {
        dispatch(showAlert({
          message: 'No items from transfer order are available in inventory',
          type: 'error'
        }));
        return;
      }

      // Add an empty row at the end
      newRows.push({
        id: (currentId++).toString(),
        barcode: '',
        searchResult: null,
        quantity: 1,
        loading: false
      });

      setTransferRows(() => newRows);

      dispatch(showAlert({
        message: `Loaded ${newRows.length - 1} items from transfer order "${orderData.name || orderData.clientName}"`,
        type: 'success'
      }));

      // Now trigger smart allocation for all loaded items
      setTimeout(() => {
        handleSmartAllocate();
      }, 500);

    } catch (error: any) {
      console.error('Failed to load transfer order:', error);
      dispatch(showAlert({
        message: error.message || 'Failed to load transfer order',
        type: 'error'
      }));
    } finally {
      setLoadingTransferOrder(false);
    }
  };

  const searchBarcode = async (rowId: string, barcode: string) => {
    if (!barcode.trim()) return;

    const currentRow = transferRows.find(r => r.id === rowId);
    if (currentRow?.searchResult && currentRow.barcode === barcode) {
      return;
    }

    // Check for existing rows with the same barcode
    const existingRow = transferRows.find(r =>
      r.id !== rowId &&
      r.barcode.trim() === barcode.trim() &&
      r.selectedSource &&
      r.quantity >= (r.selectedSource.availableQuantity || 0)
    );

    if (existingRow) {
      dispatch(showAlert({
        message: `${barcode} already has maximum available quantity (${existingRow.selectedSource?.availableQuantity || 0}) in another row.`,
        type: 'warning'
      }));
      return;
    }

    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? { ...row, loading: true, error: undefined, searchResult: null, selectedSource: undefined }
        : row
    ));

    try {
      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      if (!response.ok) {
        throw new Error('Failed to search barcode');
      }

      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.message || 'Barcode not found');
      }

      const normalized = normalizeSingleSearch(result.data);
      if (!normalized) {
        const itemName = result.data?.itemName || barcode;

        setTransferRows(prev => prev.map(row =>
          row.id === rowId ? {
            ...row,
            loading: false,
            error: result.data?.itemName
              ? `${itemName} is known but not in inventory`
              : `${barcode} not found`,
            searchResult: null
          } : row
        ));

        dispatch(showAlert({
          message: result.data?.itemName
            ? `${itemName} is known but not in inventory. Cannot add to move out.`
            : `${barcode} not found. Cannot add to move out.`,
          type: 'error'
        }));
        return;
      }

      setTransferRows(prev => prev.map(row =>
        row.id === rowId
          ? { ...row, loading: false, barcode: barcode, searchResult: normalized, unitData: result.data }
          : row
      ));

      // Auto-select source if only one location available
      setTimeout(async () => {
        if (normalized.locations && normalized.locations.length === 1) {
          const location = normalized.locations[0];
          const hasIndependent = location.independentQuantity && location.independentQuantity > 0;
          const hasBoxes = location.boxes && location.boxes.length > 0;

          if (hasIndependent && !hasBoxes) {
            // Only independent quantity, auto-select it
            const selectedSource = {
              storageUnitId: location.storageUnitId,
              storageUnitName: location.storageUnitName,
              boxBarcode: normalizeBox(''),
              availableQuantity: location.independentQuantity || 0
            };
            setTransferRows(prev => prev.map(r =>
              r.id === rowId ? { ...r, selectedSource } : r
            ));
          } else if (!hasIndependent && hasBoxes && location.boxes.length === 1) {
            // Only one box, auto-select it
            const box = location.boxes[0];
            const selectedSource = {
              storageUnitId: location.storageUnitId,
              storageUnitName: location.storageUnitName,
              boxBarcode: box.boxBarcode,
              availableQuantity: box.quantity
            };
            setTransferRows(prev => prev.map(r =>
              r.id === rowId ? { ...r, selectedSource } : r
            ));
          }
        }
      }, 50);
    } catch (error: any) {
      setTransferRows(prev => {
        const filtered = prev.filter(row => row.id !== rowId);
        if (filtered.length === 0 || !filtered.some(row => !row.barcode?.trim())) {
          filtered.push({
            id: `empty-${Date.now()}`,
            barcode: '',
            searchResult: null,
            quantity: 1,
            loading: false
          });
        }
        return filtered;
      });

      dispatch(showAlert({
        message: `${barcode}: ${error.message || 'Search failed'}. Row removed.`,
        type: 'error'
      }));
    }
  };

  const filterStockedLocations = <T extends { totalQuantity?: number }>(locs?: T[]) =>
    (locs || []).filter(l => (l.totalQuantity ?? 0) > 0);

  const normalizeSingleSearch = (data: any): any | null => {
    if (!data) return null;
    const locs = filterStockedLocations(data.locations);
    if (!locs.length) return null;
    return {
      ...data,
      locations: locs
    };
  };

  const handleBarcodeChange = (rowId: string, barcode: string) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? {
            ...row,
            barcode,
            searchResult: null,
            selectedSource: undefined,
            error: undefined
          }
        : row
    ));
  };

  const handleBarcodeSubmit = (rowId: string) => {
    const row = transferRows.find(r => r.id === rowId);
    if (row && row.barcode.trim()) {
      searchBarcode(rowId, row.barcode);

      const currentIndex = transferRows.findIndex(r => r.id === rowId);
      if (currentIndex === transferRows.length - 1) {
        addNewRow();
      }
    }
  };

  const addNewRow = () => {
    const newId = Date.now().toString();
    setTransferRows(prev => [...prev, {
      id: newId,
      barcode: '',
      searchResult: null,
      quantity: 1,
      loading: false
    }]);
  };

  const removeRow = (rowId: string) => {
    if (transferRows.length === 1) return;
    setTransferRows(prev => prev.filter(row => row.id !== rowId));
  };

  const updateRowField = (rowId: string, field: keyof TransferRow, value: any) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const statusCounts = useMemo(() => {
    const totalRows = transferRows.length;
    const withBarcodes = transferRows.filter(row => row.barcode.trim()).length;
    const withSources = transferRows.filter(row => row.barcode.trim() && row.selectedSource).length;
    const readyToMove = transferRows.filter(row =>
      row.barcode.trim() && row.selectedSource && row.quantity > 0
    ).length;
    const missingSource = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.selectedSource
    ).length;
    const errors = transferRows.filter(row => row.error).length;

    return {
      totalRows,
      withBarcodes,
      withSources,
      readyToMove,
      missingSource,
      errors,
      completion: withBarcodes > 0 ? Math.round((readyToMove / withBarcodes) * 100) : 0
    };
  }, [transferRows]);

  // Smart allocation function
  const handleSmartAllocate = async () => {
    // Get rows that have barcodes and search results but no sources selected
    const rowsToAnalyze = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.selectedSource
    );

    if (rowsToAnalyze.length === 0) {
      dispatch(showAlert({
        message: 'No items without sources found to analyze',
        type: 'info'
      }));
      return;
    }

    setSmartAllocating(true);
    try {
      // Prepare payload for smart allocation API
      const items = rowsToAnalyze.map(row => ({
        barcode: row.barcode,
        quantity: row.quantity
      }));

      const payload = {
        direction: 2,
        preferSameParent: true,
        optimizeForProximity: true,
        items: items,
      };

      const response = await DataService.post('api/InventoryAllocation/analyze', payload);

      if (!response.ok) {
        throw new Error('Failed to get smart allocation recommendations');
      }

      const result = await response.json();

      if (!result?.success || !result?.data?.barcodeAllocations) {
        throw new Error('No recommendations received');
      }

      // Apply smart allocation recommendations
      applySmartAllocationRecommendations(result.data);

      dispatch(showAlert({
        message: `Smart allocation completed for ${rowsToAnalyze.length} items`,
        type: 'success'
      }));
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Smart allocation failed',
        type: 'error'
      }));
    } finally {
      setSmartAllocating(false);
    }
  };

  const applySmartAllocationRecommendations = (smartData: any) => {
    if (!smartData?.barcodeAllocations || !Array.isArray(smartData.barcodeAllocations)) {
      return;
    }

    setTransferRows(prev => prev.map(row => {
      // Find recommendation for this row
      const allocation = smartData.barcodeAllocations.find((alloc: any) =>
        alloc.barcode === row.barcode
      );

      if (!allocation || !allocation.success || !allocation.recommendations?.length) {
        return row;
      }

      // Get the best recommendation (highest combinedScore, first one in case of ties)
      const sortedRecommendations = [...allocation.recommendations].sort(
        (a: any, b: any) => {
          const scoreA = a.combinedScore || 0;
          const scoreB = b.combinedScore || 0;
          // If scores are equal, maintain original order (stable sort)
          if (scoreA === scoreB) return 0;
          return scoreB - scoreA;
        }
      );

      const bestRecommendation = sortedRecommendations[0];

      // Find matching location in search result
      const location = row.searchResult?.locations?.find((loc: any) =>
        loc.storageUnitId === bestRecommendation.storageUnitId
      );

      if (!location) {
        return {
          ...row,
          smartRecommendations: allocation.recommendations || []
        };
      }

      let selectedSource;

      // Always try to auto-select the best recommendation
      const hasIndependent = location.independentQuantity && location.independentQuantity > 0;
      const hasBoxes = location.boxes && location.boxes.length > 0;

      // Prefer independent quantity if available and sufficient
      if (hasIndependent) {
        selectedSource = {
          storageUnitId: location.storageUnitId,
          storageUnitName: location.storageUnitName,
          boxBarcode: normalizeBox(''),
          availableQuantity: location.independentQuantity
        };
      } else if (hasBoxes) {
        // Select the first available box if no independent quantity
        const firstBox = location.boxes[0];
        selectedSource = {
          storageUnitId: location.storageUnitId,
          storageUnitName: location.storageUnitName,
          boxBarcode: firstBox.boxBarcode,
          availableQuantity: firstBox.quantity
        };
      }

      if (selectedSource) {
        return {
          ...row,
          selectedSource,
          quantity: Math.min(row.quantity, selectedSource.availableQuantity),
          smartRecommended: true,
          smartSourceSelected: true,
          smartRecommendations: allocation.recommendations || []
        };
      }

      return {
        ...row,
        smartRecommendations: allocation.recommendations || []
      };
    }));
  };

  // Bulk source selection functions
  const handleSelectAllWithoutSources = () => {
    const rowsWithoutSources = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.selectedSource
    );
    const newSelection = new Set(selectedRows);
    rowsWithoutSources.forEach(row => newSelection.add(row.id));
    setSelectedRows(newSelection);
  };

  const handleBulkSourceSelection = (sourceKey: string) => {
    if (!sourceKey || selectedRows.size === 0) return;

    const { storageUnitId, boxBarcode } = parseSourceKey(sourceKey);

    setTransferRows(prev => prev.map(row => {
      if (!selectedRows.has(row.id) || !row.searchResult) return row;

      const location = row.searchResult.locations?.find(loc =>
        loc.storageUnitId === storageUnitId
      );

      if (!location) return row;

      let selectedSource;
      if (boxBarcode && boxBarcode !== '') {
        const box = location.boxes?.find(b => b.boxBarcode === boxBarcode);
        if (box) {
          selectedSource = {
            storageUnitId,
            storageUnitName: location.storageUnitName,
            boxBarcode,
            availableQuantity: box.quantity
          };
        }
      } else {
        if (location.independentQuantity && location.independentQuantity > 0) {
          selectedSource = {
            storageUnitId,
            storageUnitName: location.storageUnitName,
            boxBarcode: normalizeBox(''),
            availableQuantity: location.independentQuantity
          };
        }
      }

      if (selectedSource) {
        return {
          ...row,
          selectedSource,
          quantity: Math.min(row.quantity, selectedSource.availableQuantity)
        };
      }
      return row;
    }));

    // Clear selection after bulk operation
    setSelectedRows(new Set());
  };

  // Get available sources for bulk selection
  const getAvailableSourcesForBulk = () => {
    const sources = new Map<string, { label: string; count: number; }>();
    transferRows.forEach(row => {
      if (!selectedRows.has(row.id) || !row.searchResult) return;

      row.searchResult.locations?.forEach(location => {
        // Independent quantity
        if (location.independentQuantity && location.independentQuantity > 0) {
          const key = makeSourceKey(location.storageUnitId, '');
          const label = `${location.storageUnitName} (Independent)`;
          sources.set(key, {
            label,
            count: (sources.get(key)?.count || 0) + 1
          });
        }

        // Box quantities
        location.boxes?.forEach(box => {
          const key = makeSourceKey(location.storageUnitId, box.boxBarcode);
          const label = `${location.storageUnitName} - Box: ${box.boxBarcode}`;
          sources.set(key, {
            label,
            count: (sources.get(key)?.count || 0) + 1
          });
        });
      });
    });

    return Array.from(sources.entries()).map(([key, value]) => ({
      key,
      label: `${value.label} (${value.count} items)`,
      count: value.count
    }));
  };

  const handleRowSelection = useCallback((rowId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }
      return newSelection;
    });
  }, []);

  // Tab 0: Destination & Info
  // Render operation information form
  const renderOperationInfo = () => (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={2}>
          <TextField
            label="Date *"
            type="date"
            value={moveOutData.date}
            onChange={(e) => setMoveOutData(prev => ({ ...prev, date: e.target.value }))}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Operation Name *"
            value={moveOutData.name}
            onChange={(e) => setMoveOutData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Quarterly Move Out"
            fullWidth
            required
            error={!moveOutData.name.trim()}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Reference Number"
            value={moveOutData.refNumber}
            onChange={(e) => setMoveOutData(prev => ({ ...prev, refNumber: e.target.value }))}
            placeholder="MOVEOUT-001"
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label="Notes"
            value={moveOutData.notes}
            onChange={(e) => setMoveOutData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes"
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            options={clientsSuppliers}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : `${option.name} (${option.type.toUpperCase()})`
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  {option.type === 'client' ? <Person fontSize="small" /> : <Business fontSize="small" />}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.type.toUpperCase()} {option.code && `• ${option.code}`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            value={selectedDestination}
            onChange={(_, newValue) =>
              setSelectedDestination(typeof newValue === 'object' && newValue !== null ? newValue as ClientSupplier : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Client or Supplier *"
                placeholder="Choose destination..."
                disabled={submitting || loadingDestinations}
                required
                error={!selectedDestination}
                size="small"
              />
            )}
            loading={loadingDestinations}
          />
        </Grid>
      </Grid>
    </Box>
  );


  // Tab 1: Items to Move Out
  // Render items to move out
  const renderItemsToMoveOut = () => (
    <Stack spacing={2}>
      {/* Action Buttons */}
      <Card variant="outlined">
        <CardContent sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Actions:
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setTransferOrderDialogOpen(true);
                  loadAllTransferOrders();
                }}
                startIcon={<PlayArrow />}
                color="primary"
                disabled={submitting}
              >
                Prepare Order
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setImportDialogOpen(true)}
                startIcon={<Upload />}
                disabled={submitting}
              >
                Import Excel
              </Button>
              {/* <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleSmartAllocate}
                disabled={submitting || smartAllocating || statusCounts.missingSource === 0}
                startIcon={smartAllocating ? <CircularProgress size={16} /> : <Inventory />}
              >
                {smartAllocating ? 'Analyzing...' : `Smart Allocate (${statusCounts.missingSource})`}
              </Button> */}
              {statusCounts.missingSource > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllWithoutSources}
                  disabled={submitting}
                >
                  Select Without Sources
                </Button>
              )}
              {selectedRows.size > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Bulk Set Source</InputLabel>
                  <Select
                    value=""
                    onChange={(e) => handleBulkSourceSelection(e.target.value)}
                    label="Bulk Set Source"
                  >
                    {getAvailableSourcesForBulk().map(source => (
                      <MenuItem key={source.key} value={source.key}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
            <Chip
              label={`${selectedRows.size} Selected`}
              size="small"
              color={selectedRows.size > 0 ? "primary" : "default"}
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Main Table */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%', minHeight: '500px' }}>
        <VirtualizedMovementTable
          data={transferRows}
          itemsPerPage={20}
          height="auto"
          showPagination={true}
          emptyMessage="Start scanning barcodes to add items for move out"
          dense={true}
          rowHeight="compact"
          ensureEmptyRow={true}
          onAddNewRow={addNewRow}
          columns={[
            {
              field: 'select',
              headerName: '☐',
              width: 50,
              renderCell: (row: TransferRow) => {
                const isSelectable = row.barcode.trim() && row.searchResult;
                return (
                  <Checkbox
                    checked={selectedRows.has(row.id)}
                    onChange={(e) => handleRowSelection(row.id, e.target.checked)}
                    disabled={!isSelectable || submitting}
                    size="small"
                  />
                );
              }
            },
            {
              field: 'barcode',
              headerName: 'Barcode',
              width: 140,
              renderCell: (row: TransferRow) => (
                <TextField
                  value={row.barcode || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleBarcodeChange(row.id, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') handleBarcodeSubmit(row.id);
                  }}
                  onBlur={() => handleBarcodeSubmit(row.id)}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder="Scan barcode"
                  size="small"
                  fullWidth
                  disabled={submitting}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <QrCodeScanner sx={{ mr: 0.5, color: 'action.active', fontSize: '1rem' }} />,
                    endAdornment: row.loading && <CircularProgress size={16} />
                  }}
                  error={!!row.error}
                  sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
                />
              )
            },
            {
              field: 'details',
              headerName: 'Details',
              width: 220,
              renderCell: (row: TransferRow) => <AllPropertiesCell row={row} />
            },
            {
              field: 'source',
              headerName: 'Source Location',
              width: 200,
              renderCell: (row: TransferRow) => {
                if (!row.searchResult || !row.searchResult.locations) {
                  return <Typography variant="caption" color="text.secondary">-</Typography>;
                }

                const locations = row.searchResult.locations;
                if (locations.length === 0) {
                  return <Typography variant="caption" color="error">No inventory</Typography>;
                }

                const getSourceScore = (storageUnitId: string, boxBarcode?: string) => {
                  if (!row.smartRecommendations) return null;
                  const rec = row.smartRecommendations.find((r: any) =>
                    r.storageUnitId === storageUnitId
                  );
                  return rec?.combinedScore ?? null;
                };

                const selectedScore = row.selectedSource ?
                  getSourceScore(row.selectedSource.storageUnitId, row.selectedSource.boxBarcode) : null;

                return (
                  <Box
                    sx={{
                      border: row.smartSourceSelected ? '2px solid #9c27b0' : 'none',
                      borderRadius: 1,
                      p: row.smartSourceSelected ? 0.5 : 0
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.8rem' }}>Source</InputLabel>
                      <Select
                        value={(() => {
                          const value = row.selectedSource
                            ? makeSourceKey(row.selectedSource.storageUnitId, normalizeBox(row.selectedSource.boxBarcode))
                            : '';                          if (row.selectedSource) {
                            console.log(`Row ${row.id} - selectedSource:`, row.selectedSource, 'computed value:', value);
                          }
                          return value;
                        })()}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          if (!value) return;

                          const { storageUnitId, boxBarcode } = parseSourceKey(value as string);
                          const location = locations.find(l => l.storageUnitId === storageUnitId);
                          if (!location) return;

                          let selectedSource;
                          if (boxBarcode && boxBarcode !== '') {
                            const box = location.boxes.find(b => b.boxBarcode === boxBarcode);
                            if (box) {
                              selectedSource = {
                                storageUnitId,
                                storageUnitName: location.storageUnitName,
                                boxBarcode,
                                availableQuantity: box.quantity
                              };
                            }
                          } else {
                            selectedSource = {
                              storageUnitId,
                              storageUnitName: location.storageUnitName,
                              boxBarcode: normalizeBox(''),
                              availableQuantity: location.independentQuantity || 0
                            };
                          }

                          if (selectedSource) {
                            setTransferRows(prev => prev.map(r =>
                              r.id === row.id ? {
                                ...r,
                                selectedSource,
                                quantity: Math.min(r.quantity, selectedSource.availableQuantity),
                                smartSourceSelected: false
                              } : r
                            ));
                          }
                        }}
                        label="Source"
                        disabled={submitting}
                        sx={{ minWidth: 160, fontSize: '0.8rem' }}
                      >
                        {locations.map((location) => [
                          ...(location.independentQuantity && location.independentQuantity > 0 ? [{
                            value: makeSourceKey(location.storageUnitId, ''),
                            label: `${location.storageUnitName} (Independent: ${location.independentQuantity})`,
                            score: getSourceScore(location.storageUnitId, '')
                          }] : []),
                          ...location.boxes.map(box => ({
                            value: makeSourceKey(location.storageUnitId, box.boxBarcode),
                            label: `${location.storageUnitName} - Box: ${box.boxBarcode} (${box.quantity})`,
                            score: getSourceScore(location.storageUnitId, box.boxBarcode)
                          }))
                        ]).flat().map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8rem' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: option.score ? '140px' : '180px'
                                }}
                                title={option.label}
                              >
                                {option.label}
                              </Typography>
                              {option.score && (
                                <Chip
                                  label={`${Math.round(option.score)}%`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.55rem',
                                    height: 14,
                                    minWidth: 32,
                                    '& .MuiChip-label': { px: 0.5 }
                                  }}
                                />
                              )}
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedScore && (
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={`Score: ${Math.round(selectedScore)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            fontSize: '0.55rem',
                            height: 16,
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              }
            },
            {
              field: 'quantity',
              headerName: 'Qty',
              width: 90,
              renderCell: (row: TransferRow) => (
                <TextField
                  type="number"
                  value={row.quantity}
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;
                    const maxQuantity = row.selectedSource?.availableQuantity || 999;
                    updateRowField(row.id, 'quantity', Math.min(Math.max(1, newQuantity), maxQuantity));
                  }}
                  size="small"
                  inputProps={{
                    min: 1,
                    max: row.selectedSource?.availableQuantity || 999,
                    style: { width: 60, fontSize: '0.8rem' }
                  }}
                  disabled={submitting}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              )
            },
            {
              field: 'available',
              headerName: 'Available',
              width: 90,
              renderCell: (row: TransferRow) => {
                if (!row.selectedSource) {
                  return <Typography variant="caption" color="text.secondary">-</Typography>;
                }
                return (
                  <Chip
                    label={row.selectedSource.availableQuantity}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20, minWidth: 40 }}
                  />
                );
              }
            },
            {
              field: 'actions',
              headerName: '',
              width: 60,
              renderCell: (row: TransferRow) => (
                <Stack direction="row" spacing={0.5}>
                  {transferRows.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.id)}
                      disabled={submitting}
                      color="error"
                      sx={{ p: 0.5 }}
                    >
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                </Stack>
              )
            }
          ]}
        />
      </Box>
    </Stack>
  );

  const validateMoveOut = (): string[] => {
    const errors: string[] = [];

    if (!moveOutData.name.trim()) {
      errors.push('Operation name is required');
    }

    if (!selectedDestination) {
      errors.push('Please select a client or supplier destination');
    }

    const allRows = transferRows.slice(0, -1).concat(
      transferRows.slice(-1).filter(row => row.barcode.trim())
    );
    const validRows = allRows.filter(row => row.barcode.trim());

    if (validRows.length === 0) {
      errors.push('At least one item must be scanned');
    }

    validRows.forEach((row, index) => {
      if (!row.searchResult) {
        errors.push(`Row ${index + 1}: Item not found in inventory`);
      } else if (!row.selectedSource) {
        errors.push(`Row ${index + 1}: Source location not selected`);
      } else if (row.quantity <= 0) {
        errors.push(`Row ${index + 1}: Quantity must be greater than 0`);
      } else if (row.quantity > row.selectedSource.availableQuantity) {
        errors.push(`Row ${index + 1}: Quantity exceeds available inventory (${row.selectedSource.availableQuantity})`);
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateMoveOut();
    if (validationErrors.length > 0) {
      dispatch(showAlert({ message: validationErrors.join('\n'), type: 'warning' }));
      return;
    }

    setSubmitting(true);
    try {
      const allRows = transferRows.slice(0, -1).concat(
        transferRows.slice(-1).filter(row => row.barcode.trim())
      );
      const validRows = allRows.filter(row => row.barcode.trim());

      const units: any[] = [];

      validRows.forEach(row => {
        if (!row.selectedSource || !row.searchResult) return;

        const sourceStorageUnitId = row.selectedSource.storageUnitId;
        const sourceBoxRefNumber = normalizeBox(row.selectedSource.boxBarcode) || null;

        units.push({
          barcode: row.barcode,
          quantity: row.quantity,
          sourceStorageUnitId,
          sourceBoxRefNumber,
          notes: `${row.searchResult.type === 'box' ? 'Box' : 'Item'}: ${row.searchResult.itemName || row.barcode}`
        });
      });

      const payload = {
        destinationType: selectedDestination!.type === 'client' ? 4 : 3,
        destinationId: selectedDestination!.id,
        units,
        name: moveOutData.name.trim() || `Move Out to ${selectedDestination!.name}`,
        refNumber: moveOutData.refNumber.trim() || undefined,
        notes: moveOutData.notes.trim() || undefined,
        operationDate: moveOutData.date ? `${moveOutData.date}T00:00:00` : undefined
      };

      const response = await DataService.post('api/Operation/move-out', payload);

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Failed to submit move out');
      }

      const result = await response.json();

      dispatch(showAlert({
        message: `Successfully moved out ${units.length} units to ${selectedDestination!.name}`,
        type: 'success'
      }));

      // Clear the persistent state after successful submission
      clearPersistentState();

      navigate('/movements/move-out');
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to submit move out', type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = moveOutData.name.trim() !== '' && !!selectedDestination && statusCounts.readyToMove > 0;

  // Excel import handler
  const handleImport = async (importRows: ImportRow[]) => {
    if (!importRows.length) return;

    try {
      const qtyByBarcode = new Map<string, number>();
      for (const row of importRows) {
        const b = (row.barcode || '').trim();
        if (!b) continue;
        const q = Number(row.quantity) || 0;
        qtyByBarcode.set(b, (qtyByBarcode.get(b) || 0) + q);
      }
      const barcodes = [...qtyByBarcode.keys()];
      if (barcodes.length === 0) return;

      const results = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: true,
        includeBoxes: true,
      });

      const foundItems: TransferRow[] = [];
      let ignoredUnknown = 0;
      let ignoredNoInventory = 0;

      let currentId = Date.now();

      for (const r of results) {
        const qty = qtyByBarcode.get(r.barcode) ?? 0;

        if (!r.found || !r.locations || r.locations.length === 0) {
          if (r.itemName) ignoredNoInventory++;
          else ignoredUnknown++;
          continue;
        }

        const searchResult: BarcodeSearchResult = {
          type: r.type === 'box' ? 'box' : 'unit',
          itemName: r.itemName || '',
          unitId: r.unitId,
          locations: r.locations,
          unit: r.unit
        };

        foundItems.push({
          id: (currentId++).toString(),
          barcode: r.barcode,
          searchResult,
          quantity: Math.max(1, qty || 1),
          loading: false,
          unitData: r.unit
        });
      }

      setTransferRows(prev => {
        const existing = prev.filter(row => row.barcode.trim());
        return [
          ...existing,
          ...foundItems,
          {
            id: (Date.now() + foundItems.length).toString(),
            barcode: '',
            searchResult: null,
            quantity: 1,
            loading: false,
          },
        ];
      });

      const importedCount = foundItems.length;
      const ignoredTotal = ignoredUnknown + ignoredNoInventory;
      const msg =
        importedCount > 0
          ? ignoredTotal > 0
            ? `Imported ${importedCount} item(s). ${ignoredTotal} unavailable item(s) ignored.`
            : `Imported ${importedCount} item(s).`
          : `No items imported. ${ignoredTotal} unavailable item(s) ignored.`;

      dispatch(showAlert({
        message: msg,
        type: importedCount > 0 ? 'success' : 'warning',
      }));

      setImportDialogOpen(false);
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to import items',
        type: 'error',
      }));
      setImportDialogOpen(false);
      throw error;
    }
  };

  // Cancel handler with form reset
  const handleCancel = () => {
    clearPersistentState();
    navigate('/movements/move-out');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Unified Header with Operation Info, Stats, and Actions */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        {/* Top Header Bar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/movements/move-out')}  aria-label="ArrowBack">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  New Move Out
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Move out items from storage units to clients or suppliers
                  </Typography>
                  {loadingTransferOrder && (
                    <Chip
                      label="Loading transfer order..."
                      size="small"
                      color="info"
                      variant="outlined"
                      icon={<CircularProgress size={12} />}
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  )}
                  {!loadingTransferOrder && persistentState.lastUpdated > 0 && (
                    <Chip
                      label={`Auto-saved: ${new Date(persistentState.lastUpdated).toLocaleTimeString()}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              >
                {submitting ? 'Processing...' : 'Execute Move Out'}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Operation Info Row */}
        {renderOperationInfo()}
      </Paper>

      {/* Content - Single scroll area */}
      <Box sx={{ flex: 1, p: 3 }}>
        {renderItemsToMoveOut()}
      </Box>

      {/* Transfer Order Dialog */}
      <Dialog
        open={transferOrderDialogOpen}
        onClose={() => {
          if (!loadingTransferOrder) {
            setTransferOrderDialogOpen(false);
            setSelectedTransferOrder(null);
            setTransferOrderSearchText('');
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PlayArrow />
            <Typography variant="h6">Prepare Transfer Order</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select a transfer order to prepare for move out. The system will load all items and automatically allocate optimal source locations.
            </Typography>
            <Autocomplete
              options={transferOrders}
              getOptionLabel={(option) => option.name || `Order for ${option.clientName}` || option.id}
              loading={loadingTransferOrders}
              value={selectedTransferOrder}
              onChange={(_, newValue) => setSelectedTransferOrder(newValue)}
              inputValue={transferOrderSearchText}
              onInputChange={(_, newInputValue) => {
                setTransferOrderSearchText(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Transfer Order"
                  placeholder="Enter order name or client name"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTransferOrders && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack direction="column" spacing={0.5} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {option.name || `Order for ${option.clientName}`}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Typography variant="caption" color="text.secondary">
                        Client: {option.clientName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Items: {option.itemCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Qty: {option.totalQuantity || 0}
                      </Typography>
                    </Stack>
                  </Stack>
                </li>
              )}
            />
            {selectedTransferOrder && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="primary">Selected Transfer Order:</Typography>
                  {selectedTransferOrder.name && (
                    <Typography variant="body2"><strong>Name:</strong> {selectedTransferOrder.name}</Typography>
                  )}
                  <Typography variant="body2"><strong>Client:</strong> {selectedTransferOrder.clientName}</Typography>
                  <Typography variant="body2"><strong>Items:</strong> {selectedTransferOrder.itemCount || 0}</Typography>
                  <Typography variant="body2"><strong>Total Quantity:</strong> {selectedTransferOrder.totalQuantity || 0}</Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTransferOrderDialogOpen(false);
              setSelectedTransferOrder(null);
              setTransferOrderSearchText('');
            }}
            disabled={loadingTransferOrder}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedTransferOrder && handleLoadTransferOrder(selectedTransferOrder)}
            variant="contained"
            disabled={!selectedTransferOrder || loadingTransferOrder}
            startIcon={loadingTransferOrder ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {loadingTransferOrder ? 'Loading...' : 'Prepare Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Import Dialog */}
      <ExcelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        title="Import Items for Move Out"
        expectedColumns={{
          required: ['barcode', 'quantity'],
          optional: ['source']
        }}
      />
    </Box>
  );
};

export default NewMoveOutPage;